<template>
    <div>
        <div class="card">
            <div class="bg-blue-light px-1 rounded pb-2">
                <TitleButton
                        class="mt-1"
                        btnTitle="Go Back"
                        title="Edit Contra"
                        @onClickCloseButton="goToList"
                />

                <div class="row mt-1">
                    <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label for="colFormLabel" class="col-form-label">Date</label>
                        <input
                                class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
                                tabindex="1"
                                type="date"
                                placeholder=""
                                v-model="contra.date"
                        >
                    </div>

                    <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label for="colFormLabel" class="col-form-label">Voucher</label>
                        <div
                            class="input-group input-group-merge invoice-edit-input-group has-validation"
                            :class="isEmptyVoucher ? 'is-invalid' : ''"
                        >
                            <div class="input-group-text" style="background-color: #efefef; outline: none">
                                <span :class="isEmptyVoucher ? 'text-danger' : ''">{{ contra.voucher_prefix }}-</span>
                            </div>
                            <input
                                type="text"
                                class="form-control invoice-edit-input"
                                aria-describedby="validationVoucherNameFeedback"
                                placeholder=""
                                v-model="contra.voucher_serial"
                                :class="isEmptyVoucher ? 'is-invalid' : ''"
                                readonly
                            >
                        </div>
                    </div>

                    <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label for="colFormLabel" class="col-xl-3 col-form-label">Amount</label>
                        <input
                                v-model="amount"
                                @input="onInputAmount"
                                type="number"
                                class="form-control text-end"
                                placeholder="Amount"
                        >
                    </div>

                    <div class="col-12 col-sm-6 col-md-4 col-lg-3">

                        <div class="d-flex align-items-center justify-content-between filter-label">
                            <label for="colFormLabel" class="col-form-label">Party: </label>
                            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
                                <i class="fas fa-search"></i>
                            </span>
                        </div>

                        <AsyncSelect
                            placeholder="Select Party"
                            v-model="contactProfile"
                            :api-service="fetchContactProfiles"
                            :format-label="formatPatientLabel"
                        />
                    </div>

                    <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label for="colFormLabel" class="col-form-label">Business</label>
                        <v-select
                                placeholder="Select Business"
                                class="w-100"
                                :options="businesses"
                                label="name"
                                :reduce="name => name.id"
                                v-model="contra.business_id"
                        />
                    </div>
                    <div class="cols-12 col-sm-6 col-lg-4 col-xxl-3">
                        <label for="colFormLabel" class="col-form-label">Created By:</label>
                        <v-select
                            placeholder="Created By"
                            :options="companyUsers"
                            label="name"
                            :reduce="(name) => name.id"
                            v-model="contra.created_by"
                        />
                    </div>
                </div>
            </div>
            <div class="card p-2">
                <AddFormElement
                        class="mb-2"
                        :accountHeads="accountHeads"
                        v-for="(item, index) in contra.ledgers"
                        :key="index"
                        :index="index"
                        :item="item"
                />

                <div class="row mt-3">
                    <div class="col-12">
                        <div class="mb-2">
                            <label for="note" class="form-label fw-bold">Memo</label>
                            <textarea v-model="contra.note" placeholder="Memo" class="form-control" rows="2" id="note"></textarea>
                        </div>
                    </div>
                </div>

                <div v-if="contra.ledger_approval_logs && contra.ledger_approval_logs.length" class="row mt-3">
                    <div class="col-12">
                        <label class="form-label fw-bold">Approval Logs</label>
                        <div class="table-responsive">
                            <table class="table table-bordered table-sm">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Status</th>
                                        <th>Approved By</th>
                                        <th>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(log, i) in contra.ledger_approval_logs" :key="i">
                                        <td>{{ i + 1 }}</td>
                                        <td>{{ startCase(log.status) }}</td>
                                        <td>{{ log.approved_by_user && log.approved_by_user.name ? log.approved_by_user.name : '' }}</td>
                                        <td>{{ log.note }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="mt-2 text-center">
                    <hr class="mb-1">
                    <button
                            :disabled="loading || isEmptyVoucher"
                            @click="updateContra"
                            class="btn btn-primary me-1"
                    >
                        <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        Update
                    </button>
                    <button
                            @click="goToList"
                            class="btn btn-outline-secondary cancel-btn"
                    >
                        {{updateCounter === 0 ? 'Cancel' : 'Back'}}
                    </button>
                </div>
            </div>
        </div>
        <VoucherValidationModal
            ref="voucherValidationModal"
            @on-press-ok="setVoucherNumber()"
        />
        <ContactDropdownFilter
            v-if="store.state.isModalOpenThree"
            @onSearchContact="onSearchContact"
            :isFilterable="true"
            :companyRoles="companyRoles"
        />
    </div>
</template>

<script>
import handleContra from '@/services/modules/contra'
import handleContact from '@/services/modules/contact'
import handleCBusinesses from '@/services/modules/businesses'
import { inject } from 'vue'
import TitleButton from '@/components/atom/TitleButton'
import AddFormElement from '@/components/molecule/company/contra/AddFormElement'
import AddButton from '@/components/atom/AddButton'
import {editVoucherNumber, generateQuery} from "@/services/utils/voucherNumberGenerator";
import VoucherValidationModal from "@/components/molecule/company/voucher/VoucherValidationModal.vue";
import figureFormatter from "@/services/utils/figureFormatter";
import {useAsyncDropdownHelper} from "@/services/utils/asyncDropdownHelper";
import handleRole from "@/services/modules/role";
import {useStore} from "vuex";
import ContactDropdownFilter from "@/components/atom/Contact/ContactDropdownFilter.vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleImportTracker from "@/services/modules/procurement/importTracker";
import startCase from 'lodash/startCase'

export default {
    name: 'ContraCreate',
    components: {
        VoucherValidationModal,
        ContactDropdownFilter,
        AsyncSelect,
        TitleButton,
        AddFormElement,
        AddButton
    },
    data: () => ({
        updateCounter: 0,
        amount: '',
        accountHeads: [],
        contactProfiles : [],
        businesses : [],
        companyUsers : [],
        saveNewLoader: false,
        isEmptyVoucher: false,
        companyRoles: [],
        contactProfile: null,
        newVoucherNumber: '',
        contra: {
            voucher_prefix : '',
            voucher_serial : '',
            voucher_no : '',
            company_id:'',
            contact_profile_id: null,
            business_id: null,
            created_by: null,
            date: '',
            note: '',
            mop_references: 'mop',
            voucher_type: 'contra_voucher',
            ledgers: []
        }
    }),
    computed: {
        isLoading () {
            return this.loading || this.saveNewLoader
        },
        start () {
            return this.$route.query.start
        },
        end () {
            return this.$route.query.end
        },
        general () {
            return this.contra.ledgers
        },
        voucherQuery () {
            return generateQuery(this.$route.params.companyId, 'contra_voucher', 'general_contra')
        }
    },
    watch: {
        contactProfile(newContact) {
            if(!newContact) {
                this.contra.contact_profile_id = null;
                return;
            }
            this.contra.contact_profile_id = newContact.id;
        }
    },

    methods: {
        concatVoucher(){
            this.isEmptyVoucher = false;
            let voucherSerial = this.decimalFormat(this.contra.voucher_serial.toString().replace(/[^0-9\.]+/g, ''))
            if(voucherSerial === '') {
                this.isEmptyVoucher = true;
            }
            this.contra.voucher_serial = voucherSerial;
            this.contra.voucher_no = `${this.contra.voucher_prefix}-${voucherSerial}`;
        },
        
        onInputAmount () {
            this.contra.ledgers.find(i => i.credit != null).credit = this.amount
            this.contra.ledgers.find(i => i.debit != null).debit = this.amount
        },
        setData () {
            this.contra.ledgers.sort((a, b) => {
                if(a.credit == null) return 1
                if(b.debit == null) return -1
            })
            this.amount = this.contra.ledgers.find(i => i.credit !=null).credit
        },
        async getContra() {
           // return;
            try {
                this.loading = true
                let contraQuery = '?with_log=1';
                let res = await this.fetchSingleContra(this.$route.params.contraId, contraQuery)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.contra = res.data
                    this.contra.created_by = res.data.user_id
                    this.contactProfile = res.data.ledgers && res.data.ledgers.length ?  res.data.ledgers[0].party : null;
                    this.setData()
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.message)
                }
            } finally {
                this.loading = false
            }
        },
        async getAccountHeads() {
           // return;
            try {
                this.loading = true
                let res = await this.fetchAccountHeads(this.$route.params.companyId)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.accountHeads = res.data
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.message)
                }
            } finally {
                this.loading = false
            }
        },
        async getBusinesses(){
          try {
            let companyId = this.$route.params.companyId
            let res = await this.fetchBusinessList('?company_id=' + companyId)

            if(!res.status) {
            }
            if(res.status) {
              this.businesses = res.data;
            }

          } catch (err) {
            if(!err.response) {
              this.showError('Something is wrong. Check your connectivity!!')
            }
            if(err.response) {
              this.showError(err.response.message)
            }
          } finally {
            this.loading = false
          }

        },
        setVoucherNumber() {
            let voucher = editVoucherNumber(this.newVoucherNumber);
            this.contra.voucher_prefix = voucher.prefix;
            this.contra.voucher_serial = voucher.serial;
        },
        goToList() {
            this.$router.push({name: 'contra-voucher-list', 
                params: {
                    companyId: this.$route.params.companyId,
                    moduleId: this.$route.params.moduleId,
                    menuId: this.$route.params.menuId,
                    pageId: this.$route.params.pageId
                },
                query: {
                    start: this.start,
                    end: this.end
                }
            
            })
        },
        getFormData () {
            let formData = new FormData();
            this.contra._method = 'PATCH'
            Object.keys(this.contra).forEach(i => {
                if(i !== 'ledgers' && i !== 'business_id' && i !== 'contact_profile_id') {
                    formData.append(i, this.contra[i])
                }
            })
            if(this.contra.business_id) formData.append('business_id', this.contra.business_id)
            if(this.contra.contact_profile_id) formData.append('contact_profile_id', this.contra.contact_profile_id)
            let ledgers = this.general.map((ledger, index) => {
              ledger.contact_profile_id = this.contra.contact_profile_id;
              ledger.business_id = this.contra.business_id;
              ledger.mop_account_head_id = this.general[Math.abs(index-1)].account_head_id; //ledgers length must be 2
              return ledger
            })
            formData.append('general_ledgers', JSON.stringify(ledgers))
            return formData;
        },
        async updateContra () {//editContra
            this.concatVoucher();
            this.contra.company_id = this.$route.params.companyId;
            let data = this.getFormData();
            this.loading = true
            try {
                let res = await this.editContra(this.contra.id, data)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.updateCounter++
                    this.showSuccess(res.message)
                    setTimeout(() => {this.goToList()},1500)
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                    return ;
                }

                if(err.response && err.response.status !== 406) {
                    this.showError(err.response?.data.message)
                }
                if(err.response.data.data && err.response.data.data.show_modal) {
                    const data = err.response.data;
                    this.newVoucherNumber = data.data.suggested
                    this.$refs.voucherValidationModal.openModal(data.message, data.data.suggested)
                }
            } finally {
                this.loading = false
                this.saveNewLoader = false
            }
        },
        onOpenContactSearchModal () {
            this.$store.state.isModalOpenThree = true;
        },
        async getCompanyRoles () {
            const res = await this.fetchCompanyDefaultRoles(`?company_id=${this.$route.params.companyId}`);
            if (res.status) {
                this.companyRoles = res.data
            }
        },
        async getCompanyUsers () {
            const res = await this.fetchContacts(`?company_id=${this.$route.params.companyId}`);
            if (res.status) {
                this.companyUsers = res.data
            }
        },
        onSearchContact (contact) {
            this.contactProfile = contact
        }
    },
    setup() {
        const showError =  inject('showError');
        const showSuccess =  inject('showSuccess');
        const {decimalFormat} = figureFormatter();
        const { fetchContacts } = handleImportTracker();

        const {
            fetchAccountHeads,
            editContra,
            fetchSingleContra,
            loading
        } = handleContra()
        const store = useStore()

        const {
          fetchContactProfiles,
        } = handleContact()

        const {
          fetchBusinessList,
        } = handleCBusinesses()

        const { formatPatientLabel } = useAsyncDropdownHelper();
        const { fetchCompanyDefaultRoles } = handleRole();

        return {
            store,
            fetchAccountHeads,
            editContra,
            fetchContacts,
            fetchSingleContra,
            loading,
            showError,
            showSuccess,
            fetchContactProfiles,
            decimalFormat,
            fetchBusinessList,
            formatPatientLabel,
            fetchCompanyDefaultRoles,
            startCase
        }
    },

    async mounted() {
        await this.getAccountHeads()
        await this.getBusinesses()
        await this.getCompanyRoles()
        await this.getCompanyUsers()
        await this.getContra()
    
        let voucher = editVoucherNumber(this.contra.voucher_no);
        this.contra.voucher_prefix = voucher.prefix;
        this.contra.voucher_serial = voucher.serial;
    }
}
</script>

<style scoped>
    .cancel-btn{
        background-color: #F0F2F5; color: #7D7D7D;
    }
    .cancel-btn:hover{
        background-color: #e9edf5;
    }
    hr{
        margin: 0 -3%;
        width: 106%;
    }
    @media screen and (min-width: 1200px) {
    .btn-close {
        /* margin-left: 200px; */
        /* font-size: 32px; */
        /* margin-top: 5px; */
        width: 30px;
        height: 30px;
        background-color: #E4E6EB;
        border-radius: 50%;
        /* margin-right: 30px; */
    }
    .btn-close:hover {
        width: 30px;
        height: 30px;
        background-color: #D8DADF;
        border-radius: 50%;
    }
    }
    @media screen and (max-width: 720px) {
    .btn-close {
        width: 25px;
        height: 25px;
        background-color: #E4E6EB;
        border-radius: 50%;
        margin-top: -5px;
        margin-right: -5px;
    }
    }
</style>
